import { useId, type ComponentProps } from "react";
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  ArrowUpRightIcon,
  BellIcon,
  MailIcon,
  MailOpenIcon,
} from "lucide-react";
import {
  Text,
  EmptyMessage,
  Button,
  Badge,
  Switch,
  Spinner,
  cn,
} from "@sfc/ui";
import { type InboxItem, type PreferenceItem } from "./contracts";

export function NotificationBadge({
  count,
  unavailable = false,
  className,
  ...buttonProps
}: {
  count: number | undefined;
  unavailable?: boolean;
} & Omit<ComponentProps<typeof Button>, "children">) {
  const label = unavailable
    ? "Notifications, count unavailable"
    : count === undefined
      ? "Notifications"
      : `Notifications, ${count} unread`;
  return (
    <Button
      {...buttonProps}
      type="button"
      variant="nav"
      size="icon"
      className={cn("relative min-h-11 min-w-11", className)}
      aria-label={label}
      title={label}
    >
      <BellIcon className="size-5" />
      {unavailable ? (
        <Badge
          variant="warning"
          className="absolute -top-1 -right-1"
          aria-hidden="true"
        >
          !
        </Badge>
      ) : count !== undefined && count > 0 ? (
        <Badge className="absolute -top-1 -right-1" aria-hidden="true">
          {count > 99 ? "99+" : count}
        </Badge>
      ) : null}
    </Button>
  );
}

export function NotificationInbox({
  items,
  pending,
  onStateChange,
  onOpen,
  hasMore,
  onLoadMore,
  loadingMore = false,
}: {
  items: InboxItem[];
  pending: (id: string) => boolean;
  onStateChange: (
    id: string,
    state: { read?: boolean; archived?: boolean },
  ) => void;
  onOpen: (item: InboxItem) => void;
  hasMore: boolean;
  onLoadMore: () => void;
  loadingMore?: boolean;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-4">
      {items.length === 0 ? (
        <EmptyMessage variant="plain" className="text-body-md py-8 text-center">
          No notifications in this view.
        </EmptyMessage>
      ) : (
        <ul className="flex min-w-0 flex-col divide-y divide-border">
          {items.map((item) => (
            <li key={item.id} className="flex min-w-0 flex-col gap-2 py-4">
              <div className="flex items-start justify-between gap-3">
                <h3
                  className={cn(
                    "body-md min-w-0 break-words",
                    item.readAt === null && "font-semibold",
                  )}
                >
                  {item.title}
                </h3>
                {item.readAt === null && (
                  <span
                    className="flex h-5 shrink-0 items-center"
                    title="Unread"
                  >
                    <span
                      className="size-1.5 rounded-full bg-accent"
                      aria-hidden="true"
                    />
                    <span className="sr-only">Unread</span>
                  </span>
                )}
              </div>
              <Text
                variant="body-sm"
                tone="secondary"
                className="whitespace-pre-wrap break-words"
              >
                {item.body}
              </Text>
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                <time
                  className="body-sm text-text-muted"
                  dateTime={item.createdAt}
                  title={new Date(item.createdAt).toLocaleString()}
                >
                  {new Date(item.createdAt).toLocaleString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </time>
                <div className="flex items-center gap-1">
                  {item.href && (
                    <Button
                      variant="quiet"
                      size="sm"
                      onClick={() => onOpen(item)}
                      disabled={pending(item.id)}
                    >
                      Open <ArrowUpRightIcon aria-hidden="true" />
                    </Button>
                  )}
                  <Button
                    variant="quiet"
                    size="icon-sm"
                    title={item.readAt === null ? "Mark read" : "Mark unread"}
                    disabled={pending(item.id)}
                    onClick={() =>
                      onStateChange(item.id, { read: item.readAt === null })
                    }
                  >
                    {item.readAt === null ? (
                      <MailOpenIcon aria-hidden="true" />
                    ) : (
                      <MailIcon aria-hidden="true" />
                    )}
                    <span className="sr-only">
                      {item.readAt === null ? "Mark read" : "Mark unread"}
                    </span>
                  </Button>
                  <Button
                    variant="quiet"
                    size="icon-sm"
                    title={item.archivedAt === null ? "Archive" : "Restore"}
                    disabled={pending(item.id)}
                    onClick={() =>
                      onStateChange(item.id, {
                        archived: item.archivedAt === null,
                      })
                    }
                  >
                    {item.archivedAt === null ? (
                      <ArchiveIcon aria-hidden="true" />
                    ) : (
                      <ArchiveRestoreIcon aria-hidden="true" />
                    )}
                    <span className="sr-only">
                      {item.archivedAt === null ? "Archive" : "Restore"}
                    </span>
                  </Button>
                  {pending(item.id) && (
                    <Spinner aria-label="Updating notification" />
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {hasMore && (
        <Button variant="outline" disabled={loadingMore} onClick={onLoadMore}>
          {loadingMore ? "Loading more" : "Next page"}
        </Button>
      )}
    </div>
  );
}

export function NotificationPreferences({
  items,
  pending,
  onChange,
}: {
  items: PreferenceItem[];
  pending: (type: string) => boolean;
  onChange: (
    type: string,
    channel: "inApp" | "email",
    enabled: boolean,
  ) => void;
}) {
  const controlId = useId();
  return (
    <div className="flex flex-col gap-5">
      <Text variant="body-sm" tone="secondary">
        Choose how to receive each notification. Changes apply to future
        notifications; turning off email also stops emails that have not started
        sending.
      </Text>
      {items.map((item) => (
        <fieldset
          key={item.type}
          disabled={pending(item.type)}
          className="flex flex-col gap-3 rounded-lg border border-border p-4"
        >
          <legend className="body-md px-1 font-medium">{item.label}</legend>
          <Text variant="body-sm" tone="secondary">
            {item.description}
          </Text>
          <div className="flex flex-wrap gap-6">
            {item.channels.map((channel) => (
              <div key={channel} className="flex min-h-11 items-center gap-3">
                <Switch
                  id={`${controlId}-${item.type}-${channel}`}
                  disabled={pending(item.type)}
                  checked={item.values[channel]}
                  onCheckedChange={(enabled) =>
                    onChange(item.type, channel, enabled)
                  }
                  aria-label={`${item.label}: ${channel === "inApp" ? "In-app" : "Email"}`}
                />
                <label
                  htmlFor={`${controlId}-${item.type}-${channel}`}
                  className="body-md"
                >
                  {channel === "inApp" ? "In-app" : "Email"}
                </label>
              </div>
            ))}
          </div>
          {pending(item.type) && (
            <Spinner aria-label="Saving notification preferences" />
          )}
        </fieldset>
      ))}
    </div>
  );
}
